import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { advanceService, Advance } from '../../services/advance.service';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function AdvanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [advance, setAdvance] = useState<Advance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');
  const [photoViewer, setPhotoViewer] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await advanceService.getAdvance(id);
      setAdvance(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const canApprove = ['sys_admin', 'general_manager', 'project_lead'].includes(
    user?.role ?? ''
  );

  const handleApprove = async () => {
    if (!id) return;
    if (!confirm('¿Aprobar este avance?')) return;
    setIsActing(true);
    try {
      await advanceService.approveAdvance(id);
      showSuccess('Avance aprobado');
      await load();
    } catch (e: any) {
      showError(e.message || 'Error al aprobar');
    } finally {
      setIsActing(false);
    }
  };

  const handleReject = async () => {
    if (!id || !reason.trim()) {
      showError('Escribe un motivo');
      return;
    }
    setIsActing(true);
    try {
      await advanceService.rejectAdvance(id, reason.trim());
      showSuccess('Avance rechazado');
      setShowReject(false);
      setReason('');
      await load();
    } catch (e: any) {
      showError(e.message || 'Error al rechazar');
    } finally {
      setIsActing(false);
    }
  };

  if (loading) return <LoadingState message="Cargando avance…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!advance) return <ErrorState message="Avance no encontrado" onRetry={load} />;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 flex items-center gap-1"
      >
        ← Volver
      </button>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">
              {advance.project_name ?? advance.project_id.slice(0, 8)}
            </h1>
            <p className="text-xs text-slate-500">
              Semana {advance.week_number} · {advance.year}
            </p>
          </div>
          <StatusBadge status={advance.status} />
        </div>

        <div className="text-xs text-slate-500 mb-3">
          {formatDate(advance.week_start)} — {formatDate(advance.week_end)}
        </div>

        <div className="bg-primary/5 rounded-lg p-3 mb-3">
          <p className="text-xs text-slate-600 mb-0.5">Avance reportado</p>
          <p className="text-2xl font-bold text-primary">{advance.progress_percentage}%</p>
        </div>

        {advance.description && (
          <div className="mb-3">
            <p className="text-xs text-slate-500 mb-1">Descripción</p>
            <p className="text-sm text-slate-700 leading-relaxed">{advance.description}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-slate-100">
          <div>
            <p className="text-xs text-slate-500">Contratista</p>
            <p className="text-slate-800 truncate">
              {advance.contractor_name ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Especialidad</p>
            <p className="text-slate-800 truncate">
              {advance.contractor_specialty ?? '—'}
            </p>
          </div>
        </div>

        {advance.rejected_reason && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-1">Motivo de rechazo</p>
            <p className="text-sm text-red-700">{advance.rejected_reason}</p>
          </div>
        )}
      </div>

      {advance.photos.length > 0 && (
        <section className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="text-sm font-bold text-slate-900 mb-2">
            Fotos ({advance.photos.length})
          </h2>
          <div className="grid grid-cols-3 gap-2">
            {advance.photos.map((photo) => (
              <button
                key={photo.id}
                onClick={() => setPhotoViewer(photo.url)}
                className="aspect-square rounded-lg overflow-hidden bg-slate-100"
              >
                <img
                  src={photo.thumbnail_url || photo.url}
                  alt="Foto"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {advance.status === 'pending' && canApprove && (
        <div className="flex gap-2">
          <button
            onClick={() => setShowReject(true)}
            disabled={isActing}
            className="flex-1 py-3 rounded-lg bg-red-50 text-red-600 font-semibold text-sm disabled:opacity-50"
          >
            Rechazar
          </button>
          <button
            onClick={handleApprove}
            disabled={isActing}
            className="flex-1 py-3 rounded-lg bg-emerald-600 text-white font-semibold text-sm disabled:opacity-50"
          >
            {isActing ? 'Procesando…' : 'Aprobar'}
          </button>
        </div>
      )}

      {showReject && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5">
            <h2 className="text-lg font-bold mb-3">Motivo de rechazo</h2>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explica el motivo…"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowReject(false)}
                className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={isActing || !reason.trim()}
                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white font-semibold text-sm disabled:opacity-50"
              >
                {isActing ? 'Procesando…' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {photoViewer && (
        <div
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setPhotoViewer(null)}
        >
          <img
            src={photoViewer}
            alt="Foto"
            className="max-w-full max-h-full object-contain"
          />
          <button
            onClick={() => setPhotoViewer(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white text-xl"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}